/**
 * The route the user was on immediately before the current one.
 *
 * Offers and Offer History restore the user's tab + scroll position only when
 * they arrive DIRECTLY from one of their own detail pages. That can't be read
 * from the browser's back stack: the detail pages' "Back to Offers" /
 * "Back to Offer History" buttons are links (a fresh navigation), not
 * history.back(). So App.js records every pathname change here, and a page
 * asks "where did the user just come from?" on mount.
 *
 * Module state, not storage: it only has to survive in-app navigation, and a
 * full reload correctly starts with no previous route.
 */
// Identifies this page load. navCount restarts on a full reload while
// sessionStorage survives it, so snapshots carry the load they were taken in
// and are only honored within that same load.
export const LOAD_ID = Math.random().toString(36).slice(2, 10);

let current = null;
let previous = null;
// Count of route changes recorded so far; with `trail` below, lets a page ask
// "has the user stayed within a set of related pages since index N?"
// (stayedWithin) and "is this a later page than N?" (getNavIndex).
let navCount = 0;

// Recent recorded routes with their index, newest last (bounded).
const trail = [];
const TRAIL_MAX = 100;

export function recordRoute(pathname) {
  if (pathname === current) return;
  previous = current;
  current = pathname;
  navCount += 1;
  trail.push({ index: navCount, path: pathname });
  if (trail.length > TRAIL_MAX) trail.shift();
}

/**
 * True when every route visited AFTER `sinceIndex` (up to and including the
 * page now on screen) matches one of `prefixes` — i.e. the user never left a
 * set of related pages since that point. Used so a drill-down chain
 * (Offers -> Offer History -> an offer -> back -> back) still restores, while
 * any detour outside the set counts as a fresh visit. False if the trail no
 * longer reaches back that far.
 */
export function stayedWithin(sinceIndex, prefixes) {
  const matches = (path) => !!path && prefixes.some((prefix) => path.startsWith(prefix));
  const oldest = trail.length ? trail[0].index : navCount + 1;
  if (sinceIndex + 1 < oldest && sinceIndex < navCount) return false;
  for (const entry of trail) {
    if (entry.index > sinceIndex && !matches(entry.path)) return false;
  }
  const here = typeof window !== 'undefined' ? window.location.pathname : null;
  return matches(here);
}

/**
 * Index of the route currently on screen, robust to the same render/effect
 * timing as getPreviousPath: while a new page is first rendering, App.js has
 * not recorded it yet, so it is navCount + 1.
 */
export function getNavIndex() {
  const here = typeof window !== 'undefined' ? window.location.pathname : null;
  return current && current !== here ? navCount + 1 : navCount;
}

/**
 * Robust to timing: a page asks this while it is first RENDERING, which is
 * before App.js's effect records the new route (child renders and effects run
 * before the parent's effect). At that moment `current` is still the page the
 * user came from; after recording, it's `previous`. Comparing against the
 * real address bar answers correctly either way.
 */
export function getPreviousPath() {
  const here = typeof window !== 'undefined' ? window.location.pathname : null;
  return current && current !== here ? current : previous;
}

/** True when the route before this one starts with any of the given prefixes. */
export function cameFrom(prefixes) {
  const prev = getPreviousPath();
  return !!prev && prefixes.some((prefix) => prev.startsWith(prefix));
}

// Test-only reset.
export function __resetRouteTrail() {
  current = null;
  previous = null;
  navCount = 0;
  trail.length = 0;
}
